// ---------------------------------------------------------------------------
// Public constants
// ---------------------------------------------------------------------------

/**
 * Base CDN URL used to load Monaco scripts and styles.
 */
export const CDN = 'https://cdn.econnect.eu/core/monaco/0.55.1';

// ---------------------------------------------------------------------------
// Internal typings for AMD loader (minimal subset)
// ---------------------------------------------------------------------------

/**
 * Minimal shape of the AMD `require` function exposed by Monaco's loader.
 */
export interface MonacoAmdRequire {
  (deps: string[], callback: (...modules: unknown[]) => void): void;
  config: (cfg: { paths: Record<string, string> }) => void;
}

/**
 * Globally shared boot state for Monaco and its AMD loader.
 */
interface MonacoBootState {
  /** Captured AMD `require` function from loader.js. */
  amdRequire: MonacoAmdRequire | null;
  /** Promise that resolves when the AMD loader has been loaded and captured. */
  amdLoaderReady: Promise<void> | null;
  /** Promise that resolves when `vs/editor/editor.main` has finished loading. */
  monacoReady: Promise<void> | null;
  /** Cached CSS text of `editor.main.css`. */
  cssText: string | null;
  /** Promise that resolves with the CSS text once fetched. */
  cssPromise: Promise<string> | null;
}

/**
 * Typed, globally shared Monaco boot bucket on `globalThis`.
 */
const g: MonacoBootState = ((globalThis as any).__MONACO_BOOT__ ??= {
  amdRequire: null,
  amdLoaderReady: null,
  monacoReady: null,
  cssText: null,
  cssPromise: null
});

// ---------------------------------------------------------------------------
// Load AMD loader (without poisoning global require)
// ---------------------------------------------------------------------------

/**
 * Loads Monaco's AMD loader (`vs/loader.js`) once and captures the private
 * `require` function into the shared boot state.
 *
 * The global `require` / `requirejs` are removed afterwards to avoid
 * interfering with other libraries or SPA environments.
 */
export function loadAmdPrivate(): Promise<void> {
  // Existing load?
  if (g.amdLoaderReady) return g.amdLoaderReady;

  g.amdLoaderReady = new Promise<void>((resolve, reject) => {
    // If require was already captured
    if (g.amdRequire) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `${CDN}/min/vs/loader.js`;
    script.async = true;

    script.onload = () => {
      const w = globalThis as any;
      const localRequire = w.require as MonacoAmdRequire | undefined;

      if (!localRequire || typeof localRequire.config !== 'function') {
        reject(new Error('Monaco AMD loader failed to initialize'));
        return;
      }

      g.amdRequire = localRequire;

      // Remove globals so they don't break other libs or SPA environments
      delete w.require;
      delete w.requirejs;

      resolve();
    };

    script.onerror = reject;
    document.head.appendChild(script);
  });

  return g.amdLoaderReady;
}

// ---------------------------------------------------------------------------
// Load Monaco itself
// ---------------------------------------------------------------------------

/**
 * Loads the Monaco editor runtime via the captured AMD loader.
 *
 * Ensures:
 * - AMD loader is initialized (via {@link loadAmdPrivate})
 * - `vs/editor/editor.main` is loaded once
 * - `MonacoEnvironment` is configured to point workers to the CDN
 */
export function loadMonaco(): Promise<void> {
  if (g.monacoReady) return g.monacoReady;

  g.monacoReady = loadAmdPrivate().then(() => {
    if (!g.amdRequire) {
      throw new Error('Monaco AMD loader did not initialize correctly.');
    }

    const amd = g.amdRequire;

    amd.config({
      paths: { vs: `${CDN}/min/vs` }
    });

    // Tell Monaco where workers live
    (globalThis as any).MonacoEnvironment = {
      getWorkerUrl() {
        return `${CDN}/min/vs/workers-DcJshg-q.js`;
      }
    };

    // Load editor.main via AMD
    return new Promise<void>(resolve => {
      amd(['vs/editor/editor.main'], () => resolve());
    });
  });

  return g.monacoReady;
}

// ---------------------------------------------------------------------------
// Load Monaco CSS into a given shadow root (fetch once, inject many)
// ---------------------------------------------------------------------------

/**
 * Ensures Monaco's `editor.main.css` is available inside the given shadow root.
 *
 * The CSS file is fetched once globally and then injected as a `<style
 * data-monaco-css>` element into each shadow root that needs it.
 *
 * @param shadow - Shadow root that hosts the editor container.
 */
export async function loadMonacoCss(shadow: ShadowRoot): Promise<void> {
  // Shadow already has it?
  if (shadow.querySelector('style[data-monaco-css]')) return;

  // Fetch once, shared globally
  g.cssPromise ??= fetch(`${CDN}/min/vs/editor/editor.main.css`)
    .then(r => r.text())
    .then(css => {
      g.cssText = css;
      return css;
    });

  // Wait for the shared CSS fetch
  const cssText = await g.cssPromise;

  const style = document.createElement('style');
  style.dataset.monacoCss = 'true';
  style.textContent = cssText;

  shadow.appendChild(style);
}
